/**
 * Viewport Detection & Lazy Loading Utilities
 * Helps with lazy loading components and images based on viewport visibility
 */

"use client";

import { useEffect, useRef, useState } from "react";

/**
 * Hook to detect if element is in viewport
 * Useful for lazy loading animations and content
 */
export const useInView = (options?: IntersectionObserverInit) => {
  const ref = useRef<HTMLDivElement>(null);
  const [isInView, setIsInView] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsInView(entry.isIntersecting);
      },
      { threshold: 0.1, ...options }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      if (ref.current) {
        observer.unobserve(ref.current);
      }
    };
  }, [options]);

  return [ref, isInView] as const;
};

/**
 * Hook to check if user prefers reduced motion
 * Disables animations for accessibility
 */
export const usePrefersReducedMotion = () => {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    setPrefersReducedMotion(mediaQuery.matches);

    const handleChange = (e: MediaQueryListEvent) => {
      setPrefersReducedMotion(e.matches);
    };

    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);

  return prefersReducedMotion;
};

/**
 * Hook to detect if the screen is mobile
 */
export const useIsMobile = (breakpoint = 768) => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < breakpoint);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, [breakpoint]);

  return isMobile;
};

/**
 * Hook to defer non-critical rendering
 * Uses requestIdleCallback or setTimeout as fallback
 */
export const useDeferredRender = (callback: () => void, delay = 0) => {
  useEffect(() => {
    const render = () => {
      if ("requestIdleCallback" in window) {
        requestIdleCallback(callback);
      } else {
        setTimeout(callback, delay);
      }
    };

    render();
  }, [callback, delay]);
};

/**
 * Component wrapper for lazy loading with intersection observer
 */
export const LazyLoad = ({
  children,
  placeholder,
}: {
  children: React.ReactNode;
  placeholder?: React.ReactNode;
}) => {
  const [ref, isInView] = useInView();

  return <div ref={ref}>{isInView ? children : placeholder}</div>;
};
